import type { Translations } from '../en/index.js';

export const fr: Translations = {
  common: {
    nav: {
      work: 'Travaux',
      approach: 'Approche',
      about: 'À propos',
      hire: 'Me recruter',
      menu: 'Menu',
    },
    localeLabel: 'Langue',
    skipToContent: 'Aller au contenu',
    portraitAlt: 'Ahmad El Masri',
    footer: {
      rights: '© 2026 Ahmad El Masri · Eindhoven, Pays-Bas',
      photography: 'Portraits par {{photographer}}',
      privacy:
        "Ce site compte les visites avec une solution d'analyse auto-hébergée. Pas de cookies, pas de suivi d'un jour à l'autre, et rien qui vous identifie.",
    },
    mobileBar: {
      status: 'Disponible pour des missions de lead et de conseil',
      cta: 'Me recruter',
    },
  },

  hero: {
    availability: 'Eindhoven · disponible pour des missions de lead et de conseil',
    nameLine1: 'Ahmad',
    nameLine2: 'El Masri',
    lede: 'Dix ans à transformer des données machine bruyantes en systèmes dignes de confiance.',
  },

  stats: {
    throughput: "messages à travers le broker que j'ai reconstruit",
    database: 'de base de données plus rapide, à matériel égal',
    deploy: 'pour déployer, contre une journée entière',
    engineers: 'ingénieurs encadrés sur trois produits',
  },

  work: {
    titleLine1: 'Travaux',
    titleLine2: 'choisis',
    lede: "Trois systèmes, chacun un problème de charge qui a cessé d'en être un.",
    cases: {
      'sg-wireless': {
        title: 'Un million de messages par seconde, et de la marge',
        body: "Le broker saturait bien avant la flotte d'appareils. J'ai rendu le broker et le proxy sans état pour qu'ils passent à l'échelle à la demande, séparé l'ingestion du trafic de requêtes, et réindexé selon la façon dont les données étaient réellement lues.",
      },
      withthegrid: {
        title: 'Savoir quel actif du réseau tombera en panne ensuite',
        body: "J'ai dirigé la plateforme de supervision d'actifs et l'équipe derrière — traduire l'intention produit en une architecture qui tient à mesure que le nombre d'actifs augmente.",
        outcome: 'Passé à l’échelle sans réécriture.',
      },
      pycom: {
        title: 'Vingt ingénieurs, trois feuilles de route, un pipeline',
        body: "Arrivé comme ingénieur, parti en dirigeant le groupe. Nous avons livré à temps en automatisant les parties ennuyeuses — les déploiements sont passés d'une journée à moins d'une heure.",
        outcome: 'Trois feuilles de route, toutes dans les temps.',
      },
    },
  },

  approach: {
    title: 'Ma façon de travailler',
    lede: 'Quatre habitudes qui survivent à une vraie échéance.',
    habits: {
      measure: {
        title: 'Mesurer avant de bouger',
        body: "Trouvez le vrai goulot avant de toucher à l'architecture. La moitié du temps, « il faut tout réécrire » se révèle être un index.",
      },
      boring: {
        title: 'Ennuyeux là où ça compte',
        body: "Des outils éprouvés sur le chemin critique. Gardez la nouveauté pour les endroits où l'échec est peu coûteux et réversible.",
      },
      slices: {
        title: 'Livrer par tranches',
        body: "De petites étapes réversibles derrière un drapeau. La migration que personne n'a remarquée est celle qui a fonctionné.",
      },
      team: {
        title: "Laisser l'équipe plus forte",
        body: 'Des décisions écrites, du code relu, et des personnes parfaitement capables de faire tourner le tout sans moi.',
      },
    },
  },

  about: {
    whereTitle: 'Où je suis passé',
    beyondTitle: 'En dehors du travail',
    roles: {
      terramira: 'Ingénieur logiciel, géospatial',
      withthegrid: 'Ingénieur senior & chef d’équipe',
      'sg-wireless': 'Ingénieur senior & chef d’équipe',
      pycom: 'Ingénieur → chef d’équipe',
      iss: 'Beyrouth → Mascate',
    },
    beyond: {
      mentoring: {
        title: 'Mentorat',
        body: 'Évaluateur de projets Udacity, mentor Pluralsight — quatre ans à relire le code des autres.',
      },
      writing: {
        title: 'Écriture',
        body: "Sur la mise à l'échelle, la conduite d'équipe et les aspects ingrats de la fiabilité.",
      },
      languages: {
        title: 'Langues',
        body: 'Arabe, anglais, néerlandais, français',
      },
      education: {
        title: 'Formation',
        body: 'MSc Nanotechnologies, Grenoble · Diplôme d’ingénieur, Université Arabe de Beyrouth',
      },
    },
  },

  hire: {
    titleLine1: 'Construisons',
    titleLine2: 'quelque chose de solide',
    lede: 'Rôles de lead, missions en contrat, ou un second avis sur votre architecture.',
    github: 'GitHub',
    linkedin: 'LinkedIn',
    cv: 'CV (PDF)',
  },

  offers: {
    architecture_review: {
      eyebrow: 'Le plus demandé',
      title: 'Revue d’architecture',
      body: 'Deux semaines. Je lis le code, je parle à vos ingénieurs, et je rends un plan priorisé que vous pouvez appliquer sans moi.',
      terms: 'Forfait · à partir de 6 k€',
    },
    contract_lead: {
      eyebrow: 'Sur le terrain',
      title: 'Lead en contrat',
      body: 'De deux à six mois intégré à votre équipe — architecture, le code difficile, et du mentorat pendant que nous livrons.',
      terms: 'Tarif journalier · à discuter',
    },
    full_time: {
      eyebrow: 'Long terme',
      title: 'Poste à temps plein',
      body: "Lead ou staff engineering aux Pays-Bas ou en remote UE. Envoyez le rôle et l'équipe que vous voudriez me voir bâtir.",
      terms: 'Indisponible pour le moment',
    },
  },

  form: {
    title: 'Envoyez votre demande',
    lede: "Rien de plus — pas de lien d'agenda, pas de formulaire derrière le formulaire.",
    engagementLegend: 'Type de mission',
    engagement: {
      architecture_review: 'Revue d’architecture',
      contract_lead: 'Lead en contrat',
      full_time: 'Poste à temps plein',
      talk_workshop: 'Conférence / atelier',
    },
    name: 'Nom',
    namePlaceholder: 'Votre nom',
    email: 'E-mail',
    emailPlaceholder: 'vous@entreprise.com',
    company: 'Entreprise',
    companyOptional: '— facultatif',
    companyPlaceholder: 'Où vous travaillez',
    problem: 'Quel est le problème ?',
    problemPlaceholder: 'Que construisez-vous, et qu’est-ce qui bloque ?',
    when: 'Quand',
    budget: 'Budget',
    budgetOptional: '— facultatif',
    timing: {
      immediately: 'Immédiatement',
      within_a_month: 'Dans le mois',
      within_a_quarter: 'Dans le trimestre',
      exploring: 'Simple exploration',
    },
    budgets: {
      prefer_to_discuss: 'Préfère en discuter',
      under_10k: 'Moins de 10 k€',
      from_10k_to_25k: '10 k€ – 25 k€',
      from_25k_to_75k: '25 k€ – 75 k€',
      over_75k: 'Plus de 75 k€',
    },
    submit: 'Envoyer',
    sending: 'Envoi…',
    reassurance: 'Je réponds personnellement sous deux jours ouvrés. NDA sur demande.',
    dismiss: 'Fermer',
    turnstileUnavailable:
      "La vérification anti-robot n'a pas pu se charger. Désactivez votre bloqueur, ou écrivez-moi directement.",
    validation: {
      name: 'Le nom est obligatoire',
      email: 'Une adresse e-mail valide est obligatoire',
      problem: 'Décrivez le problème un peu plus en détail',
    },
    status: {
      successTitle: 'Demande envoyée',
      success: 'Envoyé. Référence {{reference}} — je réponds sous deux jours ouvrés.',
      errorTitle: 'Non envoyée',
      verification: "La vérification anti-robot n'a pas abouti. Veuillez réessayer.",
      rateLimited: 'Trop de demandes. Réessayez dans quelques minutes.',
      error: "L'envoi a échoué. Écrivez-moi directement à {{email}}.",
    },
  },

  next: {
    title: 'La suite',
    replyNote: 'Eindhoven · répond sous 2 jours',
    steps: {
      read: { title: 'Je le lis', body: 'Personnellement, sous deux jours ouvrés.' },
      answer: {
        title: 'Une réponse franche',
        body: 'Oui, non, ou « voici quelqu’un de mieux placé ».',
      },
      call: {
        title: 'Un appel de 30 minutes',
        body: "Seulement si ça colle. Pas de présentation, pas d'appel commercial.",
      },
    },
  },
};
